import { FC, Fragment, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  LinearProgress,
  TableCell,
  TableRow,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { useAssessmentStatusPolling, useRetryAssessment } from '@features/assessment';
import { useFeatureFlags } from '@features/feature-flags';
import { AssessmentStatus, FeatureFlagKey } from '@libs/constants';
import { Trans } from '@libs/i18n';
import { Assessment, GetAllAssessmentsQueries } from '@libs/types';
import {
  ArrowCounterClockwiseIcon,
  CaretDownIcon,
  CaretUpIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';

import { Badge } from '../../../../components/Badge';
import { CompactRunBar } from '../RunProgress/CompactRunBar';

import { AssessmentActions } from './AssessmentActions';
import { AssessmentStatusIcon } from './AssessmentStatusIcon';
import { AssessmentVersionRow } from './AssessmentVersionRow';
import { ScorePill } from './ScorePill';
import { useAssessmentUtils } from './useAssessmentUtils';

interface AssessmentTableRowProps {
  assessment: Assessment;
  index: number;
  params?: Partial<GetAllAssessmentsQueries>;
}

const statusBarColor = (status: AssessmentStatus, theme: Theme) => {
  switch (status) {
    case AssessmentStatus.COMPLETED:
      return theme.palette.accent.green;
    case AssessmentStatus.PROCESSING:
      return theme.palette.accent.blue;
    case AssessmentStatus.FAILED:
      return theme.palette.error.main;
    case AssessmentStatus.SUBMITTED:
      return theme.palette.warning.main;
    case AssessmentStatus.DRAFT:
    default:
      return theme.palette.black[400];
  }
};

const tertiaryButtonSx = (theme: Theme) => ({
  textTransform: 'none' as const,
  fontSize: 12,
  fontWeight: 500,
  borderRadius: 2,
  px: 1.5,
  py: 0.625,
  minWidth: 'auto',
  whiteSpace: 'nowrap' as const,
  flexShrink: 0,
  backgroundColor: theme.palette.grey[200],
  color: theme.palette.text.primary,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: theme.palette.grey[300],
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.disabled,
  },
});

export const AssessmentTableRow: FC<AssessmentTableRowProps> = observer(
  ({ assessment, index, params }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const {
      getStatusTranslationKey,
      getAssessmentStatusVariant,
      formatUpdatedTime,
      formatEvaluationDuration,
    } = useAssessmentUtils();
    const [expanded, setExpanded] = useState(false);

    useAssessmentStatusPolling(assessment.id, assessment.status, params);
    const { mutate: retryAssessment, isPending: isRetrying } = useRetryAssessment();

    const versions = assessment.recentVersions ?? [];
    const olderVersions = versions.filter((v) => v.id !== assessment.id);
    const canExpand = olderVersions.length > 0;
    const versionCount = versions.length || 1;
    const hasDraftInFamily = olderVersions.some(
      (v) => v.status === AssessmentStatus.DRAFT,
    );

    const barColor = statusBarColor(assessment.status, theme);
    // Cached query, so this is a read rather than ten requests per page. Gates
    // only the NAVIGATION: the counted bar below replaces a fabricated number
    // and rides with the deploy, because keeping a known-false bar behind a flag
    // would be choosing to keep showing it.
    const { data: features } = useFeatureFlags();
    const progressEnabled = features?.[FeatureFlagKey.ASSESSMENT_PROGRESS] ?? false;
    const baseBg =
      index % 2 === 0 ? theme.palette.background.paper : theme.palette.grey[100];
    const expandedBg = theme.palette.blue[100];
    const rowBg = expanded ? expandedBg : baseBg;

    const handleNavigateOverview = () => navigate(routes.assessmentOverview(assessment.id));
    const handleContinueDraft = () =>
      navigate(routes.assessmentDomain(assessment.id, assessment.currentDomainIndex));

    const stop = (e: React.MouseEvent) => e.stopPropagation();

    return (
      <Fragment>
        <TableRow
          onClick={() => canExpand && setExpanded((prev) => !prev)}
          sx={{
            backgroundColor: rowBg,
            cursor: canExpand ? 'pointer' : 'default',
            '& > td': {
              borderBottom: expanded
                ? 'none'
                : `1px solid ${theme.palette.grey[200]}`,
              backgroundColor: 'inherit',
            },
            '& > td:first-of-type': {
              borderInlineStart: expanded
                ? `3px solid ${theme.palette.accent.blue}`
                : '3px solid transparent',
            },
            '&:hover': {
              backgroundColor: !expanded ? theme.palette.grey[200] : rowBg,
            },
            transition: 'background-color .12s, border-color .12s',
          }}
        >
          {/* Assessment Name */}
          <TableCell sx={{ py: 2.25, pl: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: 1.25,
                  backgroundColor: expanded
                    ? theme.palette.accent.blue
                    : theme.palette.grey[300],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  visibility: canExpand ? 'visible' : 'hidden',
                  transition: 'background-color .15s',
                }}
              >
                {expanded ? (
                  <CaretUpIcon
                    size={12}
                    color={theme.palette.background.paper}
                    weight="bold"
                  />
                ) : (
                  <CaretDownIcon
                    size={12}
                    color={theme.palette.text.secondary}
                    weight="bold"
                  />
                )}
              </Box>

              <AssessmentStatusIcon
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                status={assessment.status}
              />

              <Box sx={{ minWidth: 0 }}>
                {assessment.status === AssessmentStatus.COMPLETED ||
                  assessment.status === AssessmentStatus.DRAFT ? (
                  <Typography
                    component={Link}
                    to={
                      assessment.status === AssessmentStatus.COMPLETED
                        ? routes.assessmentOverview(assessment.id)
                        : routes.assessmentDomain(assessment.id, assessment.currentDomainIndex)
                    }
                    onClick={stop}
                    title={assessment.title}
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                      fontSize: 14,
                      fontWeight: 600,
                      lineHeight: 1.3,
                      color: theme.palette.text.primary,
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                        textUnderlineOffset: '3px',
                      },
                    }}
                  >
                    {assessment.title}
                  </Typography>
                ) : (
                  <Typography
                    title={assessment.title}
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                      fontSize: 14,
                      fontWeight: 600,
                      lineHeight: 1.3,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {assessment.title}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                  {assessment.language?.label && (
                    <Badge
                      label={assessment.language.label}
                      variant="neutral"
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 11,
                      color: theme.palette.text.tertiary,
                      fontWeight: 500,
                    }}
                  >
                    <Trans i18nKey="app.assessments.versions" count={versionCount} />
                  </Typography>
                  {assessment.isLatest && versionCount > 1 && (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.375,
                        color: theme.palette.green[700],
                        fontSize: 10.5,
                        fontWeight: 600,
                        lineHeight: 1,
                      }}
                    >
                      <CheckCircleIcon size={11} weight="fill" />
                      <Trans i18nKey="app.assessments.table.latest" />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </TableCell>

          {/* Status */}
          <TableCell sx={{ py: 2.25 }}>
            <Badge
              label={<Trans i18nKey={getStatusTranslationKey(assessment.status)} />}
              variant={getAssessmentStatusVariant(assessment.status)}
            />
          </TableCell>

          {/* Progress */}
          <TableCell sx={{ py: 2.25 }}>
            {/*
              A live run gets the counted bar, which can also say "we cannot see
              this run" — a state a percentage has no way to express. Every other
              status keeps the plain determinate bar, where the number is real:
              DRAFT is domains completed, COMPLETED is 100.
            */}
            {assessment.runProgress ? (
              <CompactRunBar progress={assessment.runProgress} status={assessment.status} />
            ) : (assessment.status === AssessmentStatus.DRAFT ||
              assessment.status === AssessmentStatus.PROCESSING ||
              assessment.status === AssessmentStatus.COMPLETED ||
              assessment.status === AssessmentStatus.FAILED) ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={assessment.progress}
                  sx={{
                    flex: 1,
                    minWidth: 72,
                    height: 8,
                    borderRadius: 9999,
                    backgroundColor: theme.palette.grey[300],
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: barColor,
                      borderRadius: 9999,
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    minWidth: 32,
                    fontWeight: 500,
                    fontSize: 12,
                    color: theme.palette.text.secondary,
                  }}
                >
                  {assessment.progress}%
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: theme.palette.text.tertiary }}>
                —
              </Typography>
            )}
          </TableCell>

          {/* Score */}
          <TableCell sx={{ py: 2.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {assessment.runProgress ? (
                // Not a ScorePill: an empty pill in a column of numbers reads as
                // a score of nothing. Maturity is weighted and counts documents
                // that were never supplied, so no partial figure exists to show
                // here even in principle — saying when it will exist is the only
                // honest thing the cell can say.
                <Typography
                  variant="caption"
                  sx={{ fontSize: 12, color: theme.palette.text.tertiary, fontWeight: 500 }}
                >
                  <Trans i18nKey="app.assessment.progress.list.onCompletion" />
                </Typography>
              ) : (
                <ScorePill
                  score={
                    assessment.status === AssessmentStatus.COMPLETED
                      ? assessment.score
                      : null
                  }
                />
              )}
              {assessment.status === AssessmentStatus.COMPLETED &&
                assessment.score !== null &&
                versions.length > 1 && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 10,
                      color: theme.palette.text.tertiary,
                      fontWeight: 500,
                    }}
                  >
                    <Trans i18nKey="app.assessments.table.latest" />
                  </Typography>
                )}
            </Box>
          </TableCell>

          {/* Submitted At */}
          <TableCell sx={{ py: 2.25 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: 12, color: theme.palette.text.secondary }}
            >
              {assessment.submittedAt ? formatUpdatedTime(assessment.submittedAt) : '—'}
            </Typography>
          </TableCell>

          {/* Evaluation duration (submitted → completed) */}
          <TableCell sx={{ py: 2.25 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: 12, color: theme.palette.text.secondary }}
            >
              {formatEvaluationDuration(assessment.submittedAt, assessment.completedAt)}
            </Typography>
          </TableCell>

          {/* Created At */}
          <TableCell sx={{ py: 2.25 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: 12, color: theme.palette.text.secondary }}
            >
              {formatUpdatedTime(assessment.createdAt)}
            </Typography>
          </TableCell>

          {/* Last Updated */}
          <TableCell sx={{ py: 2.25 }}>
            <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500 }}>
              {formatUpdatedTime(assessment.updatedAt)}
            </Typography>
          </TableCell>

          {/* Actions */}
          <TableCell sx={{ py: 2.25, pr: 3 }} onClick={stop}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              {assessment.status === AssessmentStatus.COMPLETED && (
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  startIcon={<ChartBarIcon size={13} />}
                  onClick={handleNavigateOverview}
                  sx={tertiaryButtonSx(theme)}
                >
                  <Trans i18nKey="app.assessments.actions.viewResults" />
                </Button>
              )}
              {assessment.status === AssessmentStatus.DRAFT && (
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  startIcon={<PencilSimpleIcon size={13} />}
                  onClick={handleContinueDraft}
                  sx={tertiaryButtonSx(theme)}
                >
                  <Trans i18nKey="app.assessments.actions.continueAssessment" />
                </Button>
              )}
              {(assessment.status === AssessmentStatus.PROCESSING ||
                assessment.status === AssessmentStatus.SUBMITTED) && (
                // Was permanently disabled, because there was nowhere for it to
                // go. Now it opens the run screen — but only when the flag that
                // gates that route is on, or it would bounce to the dashboard.
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  startIcon={<ClockIcon size={13} />}
                  disabled={!progressEnabled}
                  onClick={
                    progressEnabled
                      ? () => navigate(routes.assessmentProgress(assessment.id))
                      : undefined
                  }
                  sx={tertiaryButtonSx(theme)}
                >
                  <Trans
                    i18nKey={
                      progressEnabled
                        ? 'app.assessment.progress.list.viewRun'
                        : getStatusTranslationKey(assessment.status)
                    }
                  />
                </Button>
              )}
              {assessment.status === AssessmentStatus.FAILED && (
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  startIcon={<ArrowCounterClockwiseIcon size={13} />}
                  sx={tertiaryButtonSx(theme)}
                  disabled={isRetrying}
                  onClick={() => retryAssessment({ assessmentId: assessment.id })}
                >
                  <Trans i18nKey="app.assessments.actions.retry" />
                </Button>
              )}

              <AssessmentActions assessment={assessment} />
            </Box>
          </TableCell>
        </TableRow>

        {expanded && canExpand && (
          <Fragment>
            <TableRow
              sx={{
                backgroundColor: theme.palette.blue[200],
                '& > td': {
                  borderBottom: `1px solid ${theme.palette.grey[300]}`,
                  backgroundColor: 'inherit',
                  borderInlineStart: `3px solid ${theme.palette.accent.blue}`,
                },
              }}
            >
              <TableCell colSpan={9} sx={{ py: 0.75, pl: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ClockIcon size={13} color={theme.palette.accent.blue} />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: theme.palette.accent.blue,
                      letterSpacing: '0.3px',
                      textTransform: 'uppercase',
                    }}
                  >
                    <Trans i18nKey="app.assessments.versionHistory.title" />
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.accent.blue,
                      color: theme.palette.background.paper,
                      borderRadius: 9999,
                      px: 0.875,
                      fontSize: 11,
                      fontWeight: 600,
                      lineHeight: '18px',
                      minWidth: 18,
                      textAlign: 'center',
                    }}
                  >
                    {versions.length}
                  </Box>
                  {hasDraftInFamily && (
                    <Typography
                      variant="caption"
                      sx={{
                        ml: 1,
                        fontSize: 11,
                        fontStyle: 'italic',
                        color: theme.palette.text.tertiary,
                      }}
                    >
                      <Trans i18nKey="app.assessments.versionHistory.draftInProgress" />
                    </Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>

            {olderVersions.map((v, vi) => (
              <AssessmentVersionRow
                key={v.id}
                version={v}
                isLast={vi === olderVersions.length - 1}
              />
            ))}
          </Fragment>
        )}
      </Fragment>
    );
  },
);
